// src/components/auth/AuthScreen.jsx
import { useState } from "react";
import toast from "react-hot-toast";
import { Button, T } from "../ui";
import {
  registerClient, registerCaptain,
  loginWithPhone, logout, formatPhone,
} from "../../services/authService";
import { uploadFile, uploadFiles } from "../../services/storageService";
import { useAuth } from "../../contexts/AuthContext";
import {
  requestNotificationPermission,
  setupForegroundNotifications,
} from "../../services/notificationService";

// ─────────────────────────────────────────────────────────────
// Shared styled components
// ─────────────────────────────────────────────────────────────

function DInput({ label, ...props }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label style={{
          fontSize: 13, fontWeight: 600, color: "#94A3B8",
          marginBottom: 5, display: "block",
        }}>
          {label}
        </label>
      )}
      <input style={{
        width: "100%", padding: "11px 14px", borderRadius: 10,
        boxSizing: "border-box",
        border: "1.5px solid rgba(255,255,255,.15)",
        background: "rgba(255,255,255,.08)",
        color: "white", fontFamily: "inherit",
        fontSize: 14, outline: "none",
      }} {...props} />
    </div>
  );
}

function PhoneInput({ value, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        fontSize: 13, fontWeight: 600, color: "#94A3B8",
        marginBottom: 5, display: "block",
      }}>
        رقم الهاتف
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{
          padding: "11px 12px", borderRadius: 10,
          border: "1.5px solid rgba(255,255,255,.15)",
          background: "rgba(255,255,255,.08)",
          color: "#94A3B8", fontSize: 14, fontWeight: 700,
          display: "flex", alignItems: "center", gap: 6,
          whiteSpace: "nowrap", userSelect: "none",
        }}>
          🇪🇬 +20
        </div>
        <input
          type="tel"
          placeholder="01XXXXXXXXX"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{
            flex: 1, padding: "11px 14px", borderRadius: 10,
            boxSizing: "border-box",
            border: "1.5px solid rgba(255,255,255,.15)",
            background: "rgba(255,255,255,.08)",
            color: "white", fontFamily: "inherit",
            fontSize: 14, outline: "none",
          }}
        />
      </div>
    </div>
  );
}

function DImageUpload({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && (
        <label style={{
          fontSize: 12, fontWeight: 600, color: "#94A3B8",
          marginBottom: 5, display: "block",
        }}>
          {label}
        </label>
      )}
      <label style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        border: `2px dashed ${value ? "#10B981" : "rgba(255,255,255,.2)"}`,
        borderRadius: 12, padding: 12, cursor: "pointer",
        background: value ? "rgba(16,185,129,.1)" : "rgba(255,255,255,.04)",
        textAlign: "center", transition: "all .2s",
      }}>
        <input type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => e.target.files[0] && onChange(e.target.files[0])} />
        {value
          ? <><div style={{ fontSize: 20 }}>✅</div>
            <span style={{ fontSize: 11, color: "#10B981", fontWeight: 600 }}>
              {value.name}
            </span></>
          : <><div style={{ fontSize: 24 }}>📷</div>
            <span style={{ fontSize: 11, color: "#64748B" }}>اضغط للرفع</span></>
        }
      </label>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function LoginForm({ setUser }) {
  const [phone, setPhone] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (phone.length < 10) { toast.error("يرجى إدخال رقم هاتف صحيح"); return; }
    if (pass.length < 6) { toast.error("كلمة المرور 6 أحرف على الأقل"); return; }

    setBusy(true);
    try {
      const profile = await loginWithPhone(phone, pass);

      if (profile.role === "captain" && profile.status === "pending") {
        toast("⏳ حسابك قيد المراجعة من قبل المدير", { icon: "⏳" });
        await logout();
        setBusy(false); return;
      }
      if (profile.disabled) {
        toast.error("حسابك معطل — تواصل مع الدعم");
        await logout();
        setBusy(false); return;
      }

      setUser(profile);
      toast.success(`مرحباً، ${profile.name}!`);
      requestNotificationPermission(profile.id)
        .then(() => setupForegroundNotifications())
        .catch(() => { });

    } catch (err) {
      const code = err.code || "";
      toast.error(
        code.includes("invalid-credential") || code.includes("user-not-found")
          ? "رقم الهاتف أو كلمة المرور غير صحيحة"
          : code.includes("too-many-requests")
            ? "محاولات كثيرة — يرجى الانتظار"
            : err.message
      );
    }
    setBusy(false);
  };

  return (
    <>
      <PhoneInput value={phone} onChange={setPhone} />
      <DInput
        label="كلمة المرور"
        type="password"
        value={pass}
        onChange={e => setPass(e.target.value)}
        placeholder="••••••••"
      />
      <Button full loading={busy} onClick={submit}>
        تسجيل الدخول →
      </Button>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ٢. Register Client
// ─────────────────────────────────────────────────────────────
function RegisterClientForm({ setUser }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("يرجى إدخال اسمك"); return; }
    if (phone.length < 10) { toast.error("يرجى إدخال رقم هاتف صحيح"); return; }
    if (pass.length < 6) { toast.error("كلمة المرور 6 أحرف على الأقل"); return; }
    if (pass !== pass2) { toast.error("كلمة المرور غير متطابقة"); return; }

    setBusy(true);
    try {
      const profile = await registerClient({
        name: name.trim(), phone, password: pass,
      });
      setUser(profile);
      toast.success("🎉 أهلاً بك في رايدو!");
      requestNotificationPermission(profile.id)
        .then(() => setupForegroundNotifications())
        .catch(() => { });
    } catch (err) {
      toast.error(
        err.code === "auth/email-already-in-use"
          ? "هذا الرقم مسجل بالفعل — يرجى تسجيل الدخول"
          : err.message
      );
    }
    setBusy(false);
  };

  return (
    <>
      <DInput
        label="الاسم الكامل"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="مثال: أحمد محمد"
      />
      <PhoneInput value={phone} onChange={setPhone} />
      <DInput
        label="كلمة المرور"
        type="password"
        value={pass}
        onChange={e => setPass(e.target.value)}
        placeholder="••••••••"
      />
      <DInput
        label="تأكيد كلمة المرور"
        type="password"
        value={pass2}
        onChange={e => setPass2(e.target.value)}
        placeholder="••••••••"
      />
      <Button full loading={busy} variant="success" onClick={submit}>
        إنشاء الحساب
      </Button>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ٣. Register Captain
// ─────────────────────────────────────────────────────────────
function RegisterCaptainForm() {
  const [f, setF] = useState({
    name: "", phone: "", pass: "", pass2: "", car: "", plate: "",
  });
  const [licFile, setLicFile] = useState(null);
  const [idFile, setIdFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [carFiles, setCarFiles] = useState([null, null]);
  const [progress, setProgress] = useState({ step: "", pct: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { toast.error("يرجى إدخال اسمك"); return; }
    if (f.phone.length < 10) { toast.error("يرجى إدخال رقم هاتف صحيح"); return; }
    if (!f.car || !f.plate) { toast.error("يرجى إدخال بيانات السيارة"); return; }
    if (f.pass.length < 6) { toast.error("كلمة المرور 6 أحرف على الأقل"); return; }
    if (f.pass !== f.pass2) { toast.error("كلمة المرور غير متطابقة"); return; }
    if (!licFile || !idFile) { toast.error("يرجى رفع رخصة القيادة والبطاقة"); return; }

    setBusy(true);
    try {
      const tmpId = `tmp_${Date.now()}`;

      setProgress({ step: "رفع رخصة القيادة...", pct: 0 });
      const licenseUrl = await uploadFile(
        licFile, `captains/${tmpId}/license`,
        p => setProgress({ step: "رفع رخصة القيادة...", pct: p })
      );

      setProgress({ step: "رفع بطاقة الهوية...", pct: 0 });
      const idUrl = await uploadFile(
        idFile, `captains/${tmpId}/id`,
        p => setProgress({ step: "رفع بطاقة الهوية...", pct: p })
      );

      let photoUrl = null;
      if (photoFile) {
        setProgress({ step: "رفع الصورة الشخصية...", pct: 0 });
        photoUrl = await uploadFile(
          photoFile, `captains/${tmpId}/photo`,
          p => setProgress({ step: "رفع الصورة الشخصية...", pct: p })
        );
      }

      let carPhotoUrls = [];
      const validCarFiles = carFiles.filter(Boolean);
      if (validCarFiles.length) {
        setProgress({ step: "رفع صور السيارة...", pct: 0 });
        carPhotoUrls = await uploadFiles(validCarFiles, `captains/${tmpId}/car`);
      }

      setProgress({ step: "إنشاء الحساب...", pct: 100 });
      await registerCaptain({
        name: f.name.trim(), phone: f.phone, password: f.pass,
        car: f.car, plate: f.plate,
        licenseUrl, idUrl, photoUrl, carPhotoUrls,
      });

      await logout();
      setSubmitted(true);

    } catch (err) {
      try { await logout(); } catch { }
      toast.error(
        err.code === "auth/email-already-in-use"
          ? "هذا الرقم مسجل بالفعل"
          : err.message || "حدث خطأ — يرجى المحاولة مرة أخرى"
      );
    }
    setBusy(false);
    setProgress({ step: "", pct: 0 });
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "8px 0" }}>
        <div style={{ fontSize: 56, marginBottom: 14 }}>⏳</div>
        <h3 style={{ color: "white", fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
          طلبك قيد المراجعة
        </h3>
        <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.7, marginBottom: 18 }}>
          تم استلام بياناتك بنجاح.<br />
          سيقوم المدير بمراجعة المستندات وتفعيل حسابك.
        </p>
        <div style={{
          background: "rgba(249,115,22,.1)", borderRadius: 12,
          padding: "14px 16px", border: "1px solid rgba(249,115,22,.25)",
          textAlign: "right",
        }}>
          <div style={{ fontSize: 13, color: "#FED7AA", lineHeight: 2 }}>
            ✅ تم إنشاء الحساب بنجاح<br />
            ✅ تم رفع جميع المستندات<br />
            ⏳ في انتظار موافقة المدير
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxHeight: 420, overflowY: "auto", paddingLeft: 4 }}>
      <DInput
        label="الاسم الكامل"
        value={f.name}
        onChange={e => set("name", e.target.value)}
        placeholder="اسمك كامل"
      />
      <PhoneInput value={f.phone} onChange={v => set("phone", v)} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <DInput label="نوع السيارة" value={f.car} onChange={e => set("car", e.target.value)} placeholder="تويوتا كامري" />
        <DInput label="رقم اللوحة" value={f.plate} onChange={e => set("plate", e.target.value)} placeholder="أ ب ج 1234" />
      </div>
      <DInput
        label="كلمة المرور"
        type="password"
        value={f.pass}
        onChange={e => set("pass", e.target.value)}
        placeholder="••••••••"
      />
      <DInput
        label="تأكيد كلمة المرور"
        type="password"
        value={f.pass2}
        onChange={e => set("pass2", e.target.value)}
        placeholder="••••••••"
      />

      <p style={{ color: "#94A3B8", fontSize: 12, fontWeight: 700, margin: "8px 0" }}>
        👤 صورة شخصية (اختياري)
      </p>
      <DImageUpload label="" value={photoFile} onChange={setPhotoFile} />

      <p style={{ color: "#94A3B8", fontSize: 12, fontWeight: 700, margin: "8px 0" }}>
        📎 المستندات الإلزامية
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <DImageUpload label="رخصة القيادة *" value={licFile} onChange={setLicFile} />
        <DImageUpload label="بطاقة الهوية *" value={idFile} onChange={setIdFile} />
      </div>

      <p style={{ color: "#94A3B8", fontSize: 12, fontWeight: 700, margin: "8px 0" }}>
        🚗 صور السيارة (صور السيارة تظهر للعميل  عند اختيارك)
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        <DImageUpload label="أمامية" value={carFiles[0]} onChange={v => setCarFiles([v, carFiles[1]])} />
        <DImageUpload label="خلفية" value={carFiles[1]} onChange={v => setCarFiles([carFiles[0], v])} />
      </div>

      {busy && progress.step && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 5, textAlign: "center" }}>
            {progress.step}
          </div>
          <div style={{ background: "rgba(255,255,255,.1)", borderRadius: 8, overflow: "hidden" }}>
            <div style={{
              height: 6, background: T.accent,
              width: `${progress.pct}%`,
              transition: "width .3s", borderRadius: 8,
            }} />
          </div>
        </div>
      )}

      <Button full variant="info" loading={busy} onClick={submit}>
        {busy ? (progress.step || "جاري الإرسال...") : "📤 إرسال الطلب للمراجعة"}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main AuthScreen
// ─────────────────────────────────────────────────────────────
export default function AuthScreen() {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");  // "login" | "register"
  const [regType, setRegType] = useState(null);     // null | "client" | "captain"

  const switchMode = (m) => { setMode(m); setRegType(null); };

  return (
    <div style={styles.bg}>
      <Orb style={{ top: "8%", right: "4%", width: 260, height: 260, color: "rgba(249,115,22,.18)" }} />
      <Orb style={{ bottom: "8%", left: "4%", width: 180, height: 180, color: "rgba(99,102,241,.1)" }} />

      <div style={{ width: "100%", maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <div style={styles.logoBox}>🚗</div>
          <h1 style={{ color: "white", fontSize: 30, fontWeight: 900, margin: 0 }}>رايدو</h1>
          <p style={{ color: "#94A3B8", fontSize: 14, margin: "5px 0 0" }}>
            مشوارك فى اماان 🤍
          </p>
        </div>

        {/* Card */}
        <div style={styles.card}>

          {/* Tabs */}
          <div style={styles.tabs}>
            {[["login", "تسجيل الدخول"], ["register", "حساب جديد"]].map(([m, l]) => (
              <button key={m} onClick={() => switchMode(m)} style={{
                ...styles.tab,
                background: mode === m ? "rgba(249,115,22,.9)" : "transparent",
                color: mode === m ? "white" : "#94A3B8",
              }}>{l}</button>
            ))}
          </div>

          {/* Login */}
          {mode === "login" && <LoginForm setUser={setUser} />}

          {/* Register — pick type */}
          {mode === "register" && !regType && (
            <div>
              <p style={{ color: "#94A3B8", textAlign: "center", marginBottom: 16, fontSize: 14 }}>
                اختر نوع حسابك
              </p>
              <div style={{ display: "flex", gap: 12 }}>
                {[
                  ["client", "👤", "عميل", "أطلب مشاوير", "#10B981"],
                  ["captain", "🚘", "كابتن", "قدّم مشاوير", "#0EA5E9"],
                ].map(([type, icon, label, sub, color]) => (
                  <button key={type} onClick={() => setRegType(type)} style={{
                    flex: 1, padding: "18px 10px", borderRadius: 14,
                    border: `1px solid ${color}44`,
                    background: `${color}18`,
                    color, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 14, fontWeight: 700, textAlign: "center",
                  }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
                    {label}
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 4, fontWeight: 400 }}>
                      {sub}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Register client */}
          {mode === "register" && regType === "client" && (
            <>
              <BackButton onBack={() => setRegType(null)} label="👤 تسجيل عميل جديد" />
              <RegisterClientForm setUser={setUser} />
            </>
          )}

          {/* Register captain */}
          {mode === "register" && regType === "captain" && (
            <>
              <BackButton onBack={() => setRegType(null)} label="🚘 تسجيل كابتن جديد" />
              <RegisterCaptainForm />
            </>
          )}

          {/* Switch link */}
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={() => switchMode(mode === "login" ? "register" : "login")}
              style={{ background: "none", border: "none", color: "#64748B", cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
              {mode === "login"
                ? <>ليس لديك حساب؟ <span style={{ color: T.accent, fontWeight: 700 }}>سجّل الآن</span></>
                : <>لديك حساب؟ <span style={{ color: T.accent, fontWeight: 700 }}>تسجيل الدخول</span></>
              }
            </button>
          </div>


          {/* Support section */}
          <div style={{ textAlign: "center", marginTop: 0, paddingTop: 16, borderTop: "1px solid rgba(0,0,0,0.05)" }}>
            <p style={{ color: "#64748B", fontSize: 12, margin: 0 }}>
              لو عندك أي مشكلة كلمنا على طول:
            </p>
            <a href="tel:01000000000" style={{
              display: "block",
              color: "#007BFF",
              fontSize: 14,
              fontWeight: 700,
              textDecoration: "none",
              marginTop: 1
            }}>
              01000000000
            </a>
          </div>


        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function BackButton({ onBack, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
      <button onClick={onBack} style={{
        padding: "6px 10px", borderRadius: 8,
        border: "1px solid rgba(255,255,255,.15)",
        background: "rgba(255,255,255,.08)",
        color: "white", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
      }}>← رجوع</button>
      <span style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{label}</span>
    </div>
  );
}

function Orb({ style }) {
  return (
    <div style={{
      position: "fixed", borderRadius: "50%", pointerEvents: "none",
      background: `radial-gradient(circle, ${style.color} 0%, transparent 70%)`,
      ...style,
    }} />
  );
}

const styles = {
  bg: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg,#0F172A 0%,#1E293B 100%)",
    padding: 20, direction: "rtl", fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: "relative", overflow: "hidden",
  },
  logoBox: {
    width: 68, height: 68, borderRadius: 20,
    background: "linear-gradient(135deg,#F97316,#EA580C)",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto 12px", boxShadow: "0 10px 30px rgba(249,115,22,.4)",
    fontSize: 30,
  },
  card: {
    background: "rgba(255,255,255,.07)", backdropFilter: "blur(20px)",
    borderRadius: 22, padding: 24, border: "1px solid rgba(255,255,255,.12)",
  },
  tabs: {
    display: "flex", background: "rgba(255,255,255,.07)",
    borderRadius: 12, padding: 4, marginBottom: 20,
  },
  tab: {
    flex: 1, padding: "9px 4px", borderRadius: 9, border: "none",
    cursor: "pointer", fontFamily: "inherit", fontSize: 13,
    fontWeight: 700, transition: "all .2s",
  },
};